import { useCallback, useEffect, useState, useMemo } from 'react';
import axios from 'common/AxiosAuth';
import ContactCheckout from './ContactCheckout';
import { Button, DateRangePicker, DescriptionBar, Grid, SelectBox } from 'components';
import { scheduleGridColumns, scheduleGridStatus } from './utils';
import { GroundStation, ResponseData, ScheduleGridData, Spacecraft } from './types';
import { ListType } from 'types/selectbox';
import { ContactSchedule } from './ContactSchedule';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isBetween from "dayjs/plugin/isBetween";
import { AlertCircleIcon, ClockOutlineIcon } from 'assets/svgs';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import ValidationModal from './ValidationModal';
import 'styles/mobile.css';

import { useTranslation } from 'react-i18next';
import { getScheduleBuffer } from 'api/scheduleBuffer';

dayjs.extend(utc);
dayjs.extend(isBetween);

const ScheduleContact = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // 시간 00:00:00으로 고정

  const [spaceCraftFilter, setSpaceCraftFilter] = useState<string>('');
  const [contactFilter, setContactFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [checkedRows, setCheckedRows] = useState<number[]>([]);
  const [date, setDate] = useState<string[]>([]);
  const [modalData, setModalData] = useState<ScheduleGridData | null>(null);
  const [spacecraftData, setSpacecraftData] = useState<number | undefined>(undefined);
  const [groundstationData, setGroundstationData] = useState<number | undefined>(undefined);
  const [isOpened, setIsOpened] = useState(false);

  const [selectSpacecraftList, setSelectSpacecraftList] = useState<ListType[]>([]);
  const [selectGroundstationList, setSelectGroundstationList] = useState<ListType[]>([]);

  const [spacecraftList, setSpacecraftList] = useState<Spacecraft[]>([]);
  const [groundstationList, setGroundstationList] = useState<GroundStation[]>([]);

  const [scheduleGridData, setScheduleGridData] = useState<ScheduleGridData[]>([]);
  const [savedCheckedData, setSavedCheckedData] = useState<ScheduleGridData[]>([]);
  const [overlappingItems, setOverlappingItems] = useState<ScheduleGridData[]>([]);
  const [bufferMinutes, setBufferMinutes] = useState<number>(5);

  const [currentTime, setCurrentTime] = useState(dayjs().utc().format('YYYY-MM-DD HH:mm:ss') + ' (UTC)');
  const [loading, setLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(dayjs().utc().format('YYYY-MM-DD HH:mm:ss') + ' (UTC)');
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  // 관리자 설정된 Duplicate Buffer(분) 조회
  useEffect(() => {
    const fetchBuffer = async () => {
      try {
        const data = await getScheduleBuffer();
        if (typeof data?.bufferMinutes === 'number') {
          setBufferMinutes(data.bufferMinutes);
        }
      } catch (e) {
        console.error('버퍼 설정 조회 실패: 기본값 5분 사용', e);
        setBufferMinutes(5);
      }
    };
    fetchBuffer();
  }, []);

  useEffect(() => {
    const fetchSpacecraftData = async () => {
      try {
        const res = await axios.get('/api/link/listAll');
        const formattedData = res.data.map((item: Spacecraft) => ({
          id: item.linkViewId,
          name: item.name,
          tleFirst: item.tleFirst,
          tleSecond: item.tleSecond,
          tleTitle: item.tleTitle,
        }));
        setSpacecraftList(formattedData);

        const selectData = res.data.map((item: Spacecraft) => ({
          value: item.linkViewId,
          label: item.name,
        }));
        setSelectSpacecraftList(selectData);
      } catch (error) {
        console.error('Error fetching spacecraft data:', error);
      }
    };

    const fetchGroundstationData = async () => {
      try {
        const res = await axios.get('/api/contactprofile/listAll');
        const formattedData = res.data.map((item: GroundStation) => ({
          id: item.contactProfileId,
          name: item.name,
          gsId: item.groundStationDto.id,
          latitudeDegrees: item.groundStationDto.latitudeDegrees,
          longitudeDegrees: item.groundStationDto.longitudeDegrees,
          altitudeMeters: item.groundStationDto.altitudeMeters,
          minContactDuration: item.minContactDuration,
          minElevationDegrees: item.minElevationDegrees,
        }));
        setGroundstationList(formattedData);
        const selectData = res.data.map((item: GroundStation) => ({
          value: item.contactProfileId,
          label: item.name,
        }));
        setSelectGroundstationList(selectData);
      } catch (error) {
        console.error('Error fetching groundstation data:', error);
      }
    };

    fetchSpacecraftData();
    fetchGroundstationData();
  }, []);

  // 모든 비교/파싱을 UTC 기준으로 통일
  const checkTimeOverlap = (
    responseData: ResponseData,
    gridData: ScheduleGridData
  ): boolean => {
    const gridStart = dayjs.utc(gridData.StartTime, "YYYY-MM-DD HH:mm:ss");
    const gridEnd = dayjs.utc(gridData.EndTime, "YYYY-MM-DD HH:mm:ss");

    const adjustedResponseStart = dayjs
      .utc(responseData.startTime, "YYYY-MM-DD HH:mm:ss")
      .subtract(bufferMinutes, 'minute');
    const adjustedResponseEnd = dayjs
      .utc(responseData.endTime, "YYYY-MM-DD HH:mm:ss")
      .add(bufferMinutes, 'minute'); // 종료 시간 +버퍼 분

    const isOverlapping = adjustedResponseStart.isBefore(gridEnd) && adjustedResponseEnd.isAfter(gridStart);

    if (isOverlapping) {
      console.log("스케줄이 겹침");
      console.log(`${bufferMinutes}분 전 시작 시간(UTC):`, adjustedResponseStart.format("YYYY-MM-DD HH:mm:ss"));
      console.log(`${bufferMinutes}분 후 종료 시간(UTC):`, adjustedResponseEnd.format("YYYY-MM-DD HH:mm:ss"));
      console.log("그리드 시작(UTC):", gridStart.format("YYYY-MM-DD HH:mm:ss"));
      console.log("그리드 종료(UTC):", gridEnd.format("YYYY-MM-DD HH:mm:ss"));
    }
    return isOverlapping;
  };

  const onClickUpdatePasses = async () => {
    if (!spacecraftData) {
      setIsOpened(true);
      setModalMessage('Please select a spacecraft.');
      return;
    }

    if (!groundstationData) {
      setIsOpened(true);
      setModalMessage('Please select a ground station.');
      return;
    }

    if (date.length !== 2) {
      setIsOpened(true);
      setModalMessage('Please select a date range.');
      return;
    }

    setLoading(true);
    setDataFetched(false);

    const selectedSpacecraft = spacecraftList.find(m => m.id === spacecraftData);
    const selectedGroundstation = groundstationList.find(m => m.id === groundstationData);

    if (selectedSpacecraft && selectedGroundstation && date.length === 2) {
      const startDate = date[0];
      const endDate = date[1];

      // 서버/계산 모두 UTC 사용
      const dayjsStartDateUTC = dayjs.utc(startDate);
      const dayjsEndDateUTC = dayjs.utc(endDate);

      const minContactDuration = selectedGroundstation.minContactDuration;

      // 서버 체크 API도 UTC 문자열로 전달
      const axiosParams = {
        start: dayjsStartDateUTC.format("YYYY-MM-DDTHH:mm:ss"),
        end: dayjsEndDateUTC.format("YYYY-MM-DDTHH:mm:ss"),
        gs: selectedGroundstation.gsId,
      };

      const response = await axios.get('/api/schedule/check', { params: axiosParams });
      const responseData: ResponseData[] = response.data;
      console.log("서버 응답 데이터 (UTC)", responseData);

      const contactScheduleParams = {
        satName: selectedSpacecraft.tleTitle,
        groundStationPosition: {
          longitude: selectedGroundstation.longitudeDegrees,
          latitude: selectedGroundstation.latitudeDegrees,
          height: selectedGroundstation.altitudeMeters,
        },
        // 계산 입력도 UTC Date
        startDate: dayjsStartDateUTC.toDate(),
        endDate: dayjsEndDateUTC.toDate(),
        minElevation: selectedGroundstation.minElevationDegrees,
      };

      console.log("=== TLE 계산 파라미터 (UTC) ===");
      console.log("시작(UTC Date):", contactScheduleParams.startDate);
      console.log("종료(UTC Date):", contactScheduleParams.endDate);
      console.log("지상국:", contactScheduleParams.groundStationPosition);

      const tle = `${selectedSpacecraft.tleFirst}\n${selectedSpacecraft.tleSecond}`;
      const contactSchedule = new ContactSchedule(tle);
      contactSchedule.updatePasses(
        contactScheduleParams.satName,
        contactScheduleParams.groundStationPosition,
        contactScheduleParams.startDate,
        contactScheduleParams.endDate,
        // contactScheduleParams.minElevation
      );

      console.log("=== Computed Passes (UTC 기반 원본) ===");
      console.log("총 개수:", contactSchedule.passes.length);
      console.log("원본 패스(UTC 시각의 Date 객체):", contactSchedule.passes);

      // UTC 검색 범위/현재시간
      const searchStartUTC = dayjs.utc(startDate).startOf('day'); // 시작일 00:00:00 UTC
      const searchEndUTC = dayjs.utc(endDate).endOf('day');       // 종료일 23:59:59 UTC
      const currentUTC = dayjs.utc();                             // 현재 UTC

      console.log("=== 검색 시간 범위 (UTC) ===");
      console.log("선택:", startDate, "~", endDate);
      console.log("검색 시작 UTC:", searchStartUTC.format("YYYY-MM-DD HH:mm:ss"));
      console.log("검색 종료 UTC:", searchEndUTC.format("YYYY-MM-DD HH:mm:ss"));
      console.log("현재 UTC:", currentUTC.format("YYYY-MM-DD HH:mm:ss"));

      // 1) 기본 필터
      const basicFilteredPasses = contactSchedule.passes
        .filter((pass) => pass.AvailableTime > minContactDuration)
        .filter((pass) => pass.MaxElevation >= selectedGroundstation.minElevationDegrees);

      console.log("=== 기본 필터 후 ===");
      console.log("패스 개수:", basicFilteredPasses.length);

      // 2) UTC 객체로 래핑
      const utcConvertedPasses = basicFilteredPasses.map((pass) => {
        const passStartUTC = dayjs.utc(pass.StartTime);
        const passEndUTC = dayjs.utc(pass.EndTime);
        return { ...pass, passStartUTC, passEndUTC };
      });

      // 3) 범위 필터 + 현재 이후
      const timeFilteredPasses = utcConvertedPasses
        .filter((pass, index) => {
          const passStart = pass.passStartUTC;
          const passEnd = pass.passEndUTC;

          const isStartTimeInRange = passStart.isBetween(searchStartUTC, searchEndUTC, null, '[]');
          const isAfterCurrentTime = passEnd.isAfter(currentUTC);

          return isStartTimeInRange && isAfterCurrentTime;
        })
        .sort((a, b) => a.passStartUTC.valueOf() - b.passStartUTC.valueOf());

      console.log("=== 범위/정렬 후 ===");
      console.log("개수:", timeFilteredPasses.length);

      // 4) 그리드 데이터 (UTC 문자열 고정)
      const gridData = timeFilteredPasses.map((pass) => ({
        ...pass,
        type: "noradTle",
        StartTime: pass.passStartUTC.format("YYYY-MM-DD HH:mm:ss"), // UTC
        EndTime: pass.passEndUTC.format("YYYY-MM-DD HH:mm:ss"),     // UTC
        MaxElevation: Math.floor(pass.MaxElevation),
        AvailableTime: pass.AvailableTime,
        spacecraft: selectedSpacecraft.name,
        groundstation: selectedGroundstation.name,
        contactProfileId: selectedGroundstation.id,
        linkId: selectedSpacecraft.id,
        isDisabled: false,
      }));

      console.log("=== 최종 그리드 데이터 (UTC) ===");
      console.log("개수:", gridData.length);

      // 5) 겹침 체크 (UTC 기준)
      const newGridItems = gridData.map(gridItem => {
        const isDisabled = responseData.some(responseItem => checkTimeOverlap(responseItem, gridItem));
        return { ...gridItem, isDisabled };
      });

      setScheduleGridData(newGridItems);
      setLoading(false);
      setDataFetched(true);

      const overlappingItems = newGridItems.filter(item => item.isDisabled);
      if (overlappingItems.length > 0) {
        console.log('겹치는 스케줄이 있습니다 (UTC):', overlappingItems);
        setOverlappingItems(overlappingItems);
      } else {
        console.log('겹치는 스케줄이 없습니다. (UTC)');
        setOverlappingItems([]);
      }
    }
  };

  useEffect(() => {
    if (groundstationData) {
      const selectedGroundstation = groundstationList.find(m => m.id === groundstationData);
      if (selectedGroundstation) {
        console.log('Selected Ground Station Position:', {
          longitude: selectedGroundstation.longitudeDegrees,
          latitude: selectedGroundstation.latitudeDegrees,
          height: selectedGroundstation.altitudeMeters
        });
      }
    }
  }, [groundstationData, groundstationList]);

  // 저장도 UTC 포맷으로 전송
  const handleSaveCheckedData = async (data: ScheduleGridData[]) => {
    console.log('Original Data (UTC):', data);
    try {
      const params = data.map(item => {
        const startTimeUTC = dayjs.utc(item.StartTime, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DD HH:mm:ss");
        const endTimeUTC = dayjs.utc(item.EndTime, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DD HH:mm:ss");

        return {
          startTime: startTimeUTC, // UTC
          endTime: endTimeUTC,     // UTC
          maxElevationDegrees: item.MaxElevation,
          availableTime: item.AvailableTime,
          contactProfileId: item.contactProfileId,
          linkId: item.linkId,
        };
      });

      await axios.post('/api/schedule/reservation', params);
      setSavedCheckedData(data);
    } catch (error) {
      console.error('Error saving checked data:', error);
      alert('Failed to save data.');
    }
  };

  const isDisabled = useCallback((data: Record<string, any>) => {
    return data['isDisabled'];
  }, []);

  const memoizedSelectSpacecraftList = useMemo(() => selectSpacecraftList, [selectSpacecraftList]);
  const memoizedSelectGroundstationList = useMemo(() => selectGroundstationList, [selectGroundstationList]);
  const memoizedScheduleGridData = useMemo(() => scheduleGridData, [scheduleGridData]);

  return (
    <div className="flex h-full w-full flex-col">
      <DescriptionBar
        className="md:px-6"
        title={t('scheduleContact.pageTitle')}
        description={t('scheduleContact.pageDescription')}
      />
      <div className="font-medium px-5 py-2 text-text-secondary flex justify-between items-center shedule-display">
        <div className="flex">
          <AlertCircleIcon aria-label="Alert" className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0" />
          <p className="whitespace-pre-wrap">
            {t('scheduleContact.alertMessage')}
          </p>
        </div>
        <div className="flex">
          <ClockOutlineIcon aria-label="Current time" className="w-5 h-5 text-blue-500 mr-2" />
          {currentTime}
        </div>
      </div>
      <div className="gap-4 p-4 md:p-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 grid-rows-2 md:grid-rows-1">
        <SelectBox
          className="max-w-none"
          bgColor="layer2"
          label="Spacecraft"
          labelTooltip=""
          list={memoizedSelectSpacecraftList}
          value={spacecraftData}
          setValue={setSpacecraftData}
        />
        <SelectBox
          className="max-w-none"
          bgColor="layer2"
          label="Ground Station"
          labelTooltip=""
          list={memoizedSelectGroundstationList}
          value={groundstationData}
          setValue={setGroundstationData}
        />
        <div className="col-span-2 xs:col-span-2 md:col-span-2 3xl:col-span-2">
          <DateRangePicker className="w-full" bgColor="layer2" label="Date" labelTooltip="Date" value={date} setValue={setDate} minDate={new Date()} />
        </div>
        <div className="flex items-end justify-start">
          <Button className="h-8" text={t('scheduleContact.button.search')} onClick={onClickUpdatePasses} type="tertiary" size="large" aria-label="search list"></Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-8 gap-4 pl-4 pr-4">
          {Array(8).fill(null).map((_, index) => (
            <div key={index} className="h-12">
              {Array(8).fill(null).map((_, innerIndex) => (
                <Skeleton key={innerIndex} className="w-full" />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <>
          {dataFetched && memoizedScheduleGridData.length === 0 ? (
            <div className="flex justify-center items-center py-10">
              <p className="text-body1 text-gray-500">
                We couldn&apos;t find any schedules matching your search criteria.
              </p>
            </div>
          ) : (
            <div className="flex w-full flex-1 flex-col justify-between">
              <Grid
                gridColumns={scheduleGridColumns}
                gridData={memoizedScheduleGridData}
                filterValue={{
                  spacecraft: spaceCraftFilter,
                  groundStation: contactFilter,
                  date: dateFilter,
                }}
                options={{
                  useFilter: true,
                  useCheckbox: true,
                  customCell: {
                    AvailableTime: (_, row) => <span>{(row as ScheduleGridData).AvailableTime + ' sec'}</span>,
                    MaxElevation: (_, row) => <span>{(row as ScheduleGridData).MaxElevation + 'º'}</span>,
                    isDisabled: (_, row) => {
                      const isDisabled = (row as ScheduleGridData).isDisabled;
                      const status = isDisabled ? scheduleGridStatus.true : scheduleGridStatus.false;
                      return (
                        <div title={status.title}>
                          <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${status.className}`}>
                            <span className="text-nowrap text-small font-regular">{status.title}</span>
                          </div>
                        </div>
                      );
                    },
                  },
                }}
                checkedRows={checkedRows}
                setCheckedRows={setCheckedRows}
                isDisabled={isDisabled}
              />
              <div className="flex w-full items-center justify-center py-3">
                <ContactCheckout
                  key={i18n.language} // 리렌더링 유도
                  plan="Free"
                  checkedData={memoizedScheduleGridData.filter((_, index) => checkedRows.includes(index))}
                  onSave={handleSaveCheckedData}
                />
              </div>
            </div>
          )}
        </>
      )}
      <ValidationModal
        isOpen={isOpened}
        message={modalMessage}
        onCancel={() => setIsOpened(false)}
      />
    </div>
  );
};

export default ScheduleContact;
